import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Don't redirect here, let the auth context handle it
    }
    return Promise.reject(error);
  }
);

export default api;

// Facility API calls
export const facilityAPI = {
  getAll: (params) => api.get('/api/facilities', { params }),
  search: (params) => api.get('/api/facilities/search', { params }),
  getNearby: (params) => api.get('/api/facilities/nearby', { params }),
  getById: (id) => api.get(`/api/facilities/${id}`),
  create: (data) => api.post('/api/facilities', data),
  update: (id, data) => api.put(`/api/facilities/${id}`, data),
  delete: (id) => api.delete(`/api/facilities/${id}`),
  addReview: (id, data) => api.post(`/api/facilities/${id}/reviews`, data),
  getTypes: () => api.get('/api/facilities/types/list'),
  getSpecialties: () => api.get('/api/facilities/specialties/list')
};

// Subscriber API calls
export const subscriberAPI = {
  subscribe: (data) => api.post('/api/subscribers', data),
  updatePreferences: (data) => api.put('/api/subscribers/preferences', data),
  getAll: (params) => api.get('/api/subscribers', { params }),
  getStats: () => api.get('/api/subscribers/stats'),
  delete: (id) => api.delete(`/api/subscribers/${id}`),
  export: (params) => api.get('/api/subscribers/export', { params, responseType: 'blob' })
};

// Analytics API calls
export const analyticsAPI = {
  track: (data) => api.post('/api/analytics/track', data),
  getDashboard: (params) => api.get('/api/analytics/dashboard', { params }),
  getVisitors: (params) => api.get('/api/analytics/visitors', { params }),
  getRecentVisitors: (params) => api.get('/api/analytics/visitors/recent', { params }),
  getRealtime: () => api.get('/api/analytics/realtime'),
  getSearchAnalytics: (params) => api.get('/api/analytics/search-analytics', { params })
};

// Admin API calls
export const adminAPI = {
  getOverview: () => api.get('/api/admin/overview'),
  getUsers: (params) => api.get('/api/admin/users', { params }),
  getUserById: (id) => api.get(`/api/admin/users/${id}`),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  toggleUserStatus: (id) => api.put(`/api/admin/users/${id}/toggle-status`),
  getFacilities: (params) => api.get('/api/admin/facilities', { params }),
  toggleFacilityVerification: (id) => api.put(`/api/admin/facilities/${id}/verify`),
  deleteFacility: (id) => api.delete(`/api/admin/facilities/${id}`)
};

// Google Places API calls (via our server proxy)
export const placesAPI = {
  search: (params) => api.get('/api/places/search', { params }),
  autocomplete: (params) => api.get('/api/places/autocomplete', { params }),
  getDetails: (placeId) => api.get(`/api/places/details/${placeId}`),
  getPhotoUrl: (ref, maxwidth = 400) => `${api.defaults.baseURL || ''}/api/places/photo?ref=${encodeURIComponent(ref)}&maxwidth=${maxwidth}`,
};

// Auth API calls
export const authAPI = {
  login: (data) => api.post('/api/auth/login', data),
  register: (data) => api.post('/api/auth/register', data),
  verifyEmail: (data) => api.post('/api/auth/verify-email', data),
  resendCode: (data) => api.post('/api/auth/resend-code', data),
  getProfile: () => api.get('/api/auth/profile'),
  updateProfile: (data) => api.put('/api/auth/profile', data)
};

// Favorites API calls
export const favoritesAPI = {
  getAll: () => api.get('/api/favorites'),
  add: (data) => api.post('/api/favorites', data),
  remove: (placeId) => api.delete(`/api/favorites/${placeId}`),
  check: (placeId) => api.get(`/api/favorites/check/${placeId}`)
};

// Ads API calls
export const adsAPI = {
  getActive: (params) => api.get('/api/ads/active', { params }),
  getPricing: (params) => api.get('/api/ads/pricing', { params }),
  create: (data) => api.post('/api/ads', data),
  submitForReview: (id) => api.post(`/api/ads/${id}/submit`),
  pay: (id) => api.post(`/api/ads/${id}/pay`),
  getMyAds: () => api.get('/api/ads/my-ads'),
  confirmPayment: (id) => api.post(`/api/ads/${id}/confirm-payment`),
  trackClick: (id) => api.post(`/api/ads/${id}/click`)
};

// Admin Ads API calls
export const adminAdsAPI = {
  getAll: (params) => api.get('/api/admin/ads', { params }),
  getById: (id) => api.get(`/api/admin/ads/${id}`),
  approve: (id) => api.put(`/api/admin/ads/${id}/approve`),
  reject: (id, reason) => api.put(`/api/admin/ads/${id}/reject`, { reason }),
  createAd: (data) => api.post('/api/admin/ads/create', data),
  editAd: (id, data) => api.put(`/api/admin/ads/${id}/edit`, data),
  pauseAd: (id) => api.put(`/api/admin/ads/${id}/pause`),
  activateAd: (id) => api.put(`/api/admin/ads/${id}/activate`),
  deleteAd: (id) => api.delete(`/api/admin/ads/${id}`),
  getPricing: () => api.get('/api/admin/ads/pricing/all'),
  createPricing: (data) => api.post('/api/admin/ads/pricing', data),
  updatePricing: (id, data) => api.put(`/api/admin/ads/pricing/${id}`, data),
  deletePricing: (id) => api.delete(`/api/admin/ads/pricing/${id}`),
  getStats: () => api.get('/api/admin/ads/stats')
};

// Contact API
export const contactAPI = {
  send: (data) => api.post('/api/contact', data),
};

// Blog API (public)
export const blogAPI = {
  getPosts: (params) => api.get('/api/blog', { params }),
  getPost: (slug) => api.get(`/api/blog/${slug}`),
  getPillars: () => api.get('/api/blog/pillars'),
  getByPillar: (slug, params) => api.get(`/api/blog/pillar/${slug}`, { params }),
  getByCategory: (slug, params) => api.get(`/api/blog/category/${slug}`, { params }),
};

// Admin Blog API
export const adminBlogAPI = {
  // Stats
  getStats: () => api.get('/api/admin/blog/stats'),
  // Pillars
  getPillars: () => api.get('/api/admin/blog/pillars'),
  createPillar: (data) => api.post('/api/admin/blog/pillars', data),
  updatePillar: (id, data) => api.put(`/api/admin/blog/pillars/${id}`, data),
  deletePillar: (id) => api.delete(`/api/admin/blog/pillars/${id}`),
  // Categories
  getCategories: (params) => api.get('/api/admin/blog/categories', { params }),
  createCategory: (data) => api.post('/api/admin/blog/categories', data),
  updateCategory: (id, data) => api.put(`/api/admin/blog/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/admin/blog/categories/${id}`),
  // Posts
  getPosts: (params) => api.get('/api/admin/blog/posts', { params }),
  getPost: (id) => api.get(`/api/admin/blog/posts/${id}`),
  createPost: (data) => api.post('/api/admin/blog/posts', data),
  updatePost: (id, data) => api.put(`/api/admin/blog/posts/${id}`, data),
  deletePost: (id) => api.delete(`/api/admin/blog/posts/${id}`),
  togglePublish: (id) => api.put(`/api/admin/blog/posts/${id}/toggle-publish`),
  // AI
  generateTopics: (data) => api.post('/api/admin/blog/ai/generate-topics', data),
  generatePost: (data) => api.post('/api/admin/blog/ai/generate-post', data),
  // Auto-Gen Settings
  getAutoGenSettings: () => api.get('/api/admin/blog/settings/auto-gen'),
  updateAutoGenSettings: (data) => api.put('/api/admin/blog/settings/auto-gen', data),
  runAutoGenNow: () => api.post('/api/admin/blog/settings/auto-gen/run-now'),
};

// Upload API
export const uploadAPI = {
  adImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/api/upload/ad-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};
